import { BATCH_ACTIONS } from 'pages/Batch/configs';

import type { IActionsPreset } from './types';
import type { DriveResourceType } from './driveClient';

// Fields whose URLs are known-permanent and should NOT be persisted to Drive.
// All other URL-like string values in step params are persisted by default.
const PERMANENT_URL_FIELDS: Partial<Record<BATCH_ACTIONS, string[]>> = {
  [BATCH_ACTIONS.wfx]: ['overlayResourceUrl'],
};

const MIME_TO_EXT: Record<string, string> = {
  'image/webp': '.webp',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
  'video/mp4': '.mp4',
  'video/webm': '.webm',
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
  'application/json': '.json',
};

const EXT_TO_RESOURCE_TYPE: Record<string, DriveResourceType> = {
  jpg: 'PHOTO',
  jpeg: 'PHOTO',
  png: 'PHOTO',
  webp: 'PHOTO',
  gif: 'PHOTO',
  svg: 'PHOTO',
  mp4: 'VIDEO',
  webm: 'VIDEO',
  mov: 'VIDEO',
  mp3: 'AUDIO',
  wav: 'AUDIO',
  ogg: 'AUDIO',
};

export interface AssetDescriptor {
  sourceUrl: string;
  driveName: string;
  location: { type: 'preview' } | { type: 'stepParam'; stepIndex: number; paramKey: string };
}

export function isUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^(https?:|blob:|data:)/i.test(value);
}

export function isPicsartUrl(url: string): boolean {
  return /picsart/i.test(url);
}

export function isBlobOrDataUrl(url: string): boolean {
  return /^(blob:|data:)/i.test(url);
}

export function getExtFromMime(mime: string): string {
  return MIME_TO_EXT[mime] || '';
}

export function getResourceType(mimeType: string): DriveResourceType {
  if (mimeType.startsWith('image/')) return 'PHOTO';
  if (mimeType.startsWith('video/')) return 'VIDEO';
  if (mimeType.startsWith('audio/')) return 'AUDIO';
  return 'OTHER';
}

export function getResourceTypeFromUrl(url: string): DriveResourceType {
  const ext = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase() ?? '';
  return EXT_TO_RESOURCE_TYPE[ext] ?? 'OTHER';
}

export function extractAssetUrls(preset: IActionsPreset): AssetDescriptor[] {
  const assets: AssetDescriptor[] = [];

  if (preset.preview.url && isUrl(preset.preview.url)) {
    assets.push({
      sourceUrl: preset.preview.url,
      driveName: 'preview',
      location: { type: 'preview' },
    });
  }

  preset.steps.forEach((step, index) => {
    const permanentFields = PERMANENT_URL_FIELDS[step.action as BATCH_ACTIONS] ?? [];

    Object.entries(step.params)
      .filter(([key, value]) => !permanentFields.includes(key) && isUrl(value))
      .forEach(([key, value]) => {
        assets.push({
          sourceUrl: value as string,
          driveName: `${key}_${index}`,
          location: { type: 'stepParam', stepIndex: index, paramKey: key },
        });
      });
  });

  return assets;
}

export function rewriteAssetUrl(preset: IActionsPreset, descriptor: AssetDescriptor, newUrl: string): void {
  if (descriptor.location.type === 'preview') {
    preset.preview.url = newUrl;
  } else {
    const { stepIndex, paramKey } = descriptor.location;
    preset.steps[stepIndex].params[paramKey] = newUrl;
  }
}
